import { Injectable, inject, signal } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import axios, { AxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { electronicFormatIBAN, isValidIBAN } from "ibantools";
import { Subscription, take } from "rxjs";
import {
  claimType,
  partyType,
  registrationOffice,
  representativeType,
} from "src/app/components/dashboard/enums/enum-descriptors";
import { Address, BankAccount, CasesService, ExternalIdType, GetEditCaseResponse, InterestType, UpdateCaseParams, UpdateRepresentative } from "src/app/services/cases/cases.service";
import { CountriesService, Country } from "src/app/services/countries/countries.service";
import { EnumsService } from "src/app/services/enums/enums.service";
import { HelpersService } from "src/app/services/helpers/helpers.service";
import { LawFirmItem } from "src/app/services/law-firm/law-firm.service";
import { Representative } from "src/app/services/representatives/representatives.service";
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from "src/app/shared/company-search-service/company-search.service";
import { environment } from "src/environments/environment";
import { ClaimType, PartyType, RepresentativeType } from "src/types";
import { RegistrationOffice } from "src/types/model/schemas";

type InvoiceOcrData = {
  client_type: 'org' | 'house';
  client_country_iso: string | null;
  client_postcode: string | null;
  client_city: string | null;
  client_street: string | null;
  client_tax_number: string | null;
  client_name: string | null;
  debtor_type: 'org' | 'ind';
  debtor_country_iso: string | null;
  debtor_postcode: string | null;
  debtor_city: string | null;
  debtor_street: string | null;
  debtor_tax_number: string | null;
  debtor_name: string | null;
  claim_original_amount: number | null;
  currency_iso: string | null;
  claim_due_date_at: string | null;
  invoice_number: string | null;
  invoice_issued_at: string | null;
  claim_type_internal_id: string | null;
};

type LegalRelationshipOcrInvoiceItem = {
  name: string | null;
  description: string | null;
};

type LegalRelationshipOcrData = {
  legal_relationship_id: number | null;
  claim_type_internal_id: string | null;
  case_type: CaseType | null;
  contract_date_at: string | null;
  contract_identifier: string | null;
  claim_identification_text: string | null;
  invoice_item: LegalRelationshipOcrInvoiceItem | null;
};

type ClaimsGlobalControls = {
  caseType: FormControl<CaseType>;
  claimType: FormControl<number | null>;
  contractIdentification: FormControl<string>;
  contractDate: FormControl<Date | string | null>;
};

type CondominiumAccountStatementOcrData = {
  is_condominium_account_statement: boolean;
  unit_address: string | null;
  debt_start_at: string | null;
  common_representative_name: string | null;
  common_representative_postcode: string | null;
  common_representative_city: string | null;
  common_representative_street: string | null;
  common_representative_type_id: number | null;
};

type EditClaim = GetEditCaseResponse['claims'][number];
type EditDebtor = GetEditCaseResponse['debtors'][number];

type UploadedFile = {
  id: string;
  file_name: string;
  storage_url: string;
};

export type LegalRelationshipFile = {
  id: string | null;
  storage_url: string | File | null;
  file_name: string | null;
};

export const CASE_TYPE = {
  INVOICE: 'invoice',
  CONTRACT: 'contract',
  CONDOMINIUM: 'condominium_account_statement',
} as const;
export type CaseType = typeof CASE_TYPE[keyof typeof CASE_TYPE];

export const CONDOMINIUM_COMMON_COST_CLAIM_TYPE_INTERNAL_ID = '76-a';

export type AddressControls = {
  country_id: FormControl<number | null>;
  postcode: FormControl<string | null>;
  settlement: FormControl<string | null>;
  street: FormControl<string | null>;
};

export type BankAccountControls = {
  account_number: FormControl<string | null>;
  bic: FormControl<string | null>;
  bank_name: FormControl<string | null>;
  lock_bank_account: FormControl<boolean>;
};

export type ExternalIdControls = {
  type: FormControl<ExternalIdType>;
  value: FormControl<string>;
};

export type RepresentativeControls = {
  company_updated_at: FormControl<string | null>;
  id: FormControl<number | null>;
  is_standalone: FormControl<boolean>;
  name: FormControl<string | null>;
  tax_number: FormControl<string | null>;
  party_type_id: FormControl<number | null>;
  registration_number: FormControl<string | null>;
  registration_office_id: FormControl<number | null>;
  type_id: FormControl<number | null>;
  address: FormGroup<AddressControls>;
  representatives: FormArray<FormGroup<RepresentativeControls>>;
};

export type ClientDataControls = {
  birth_date: FormControl<Date | string | null>;
  birth_place: FormControl<string | null>;
  mother_name: FormControl<string | null>;
  registration_number: FormControl<string | null>;
};

export type ClientControls = {
  company_updated_at: FormControl<string | null>;
  name: FormControl<string | null>;
  bank_account: FormGroup<BankAccountControls>;
  address: FormGroup<AddressControls>;
  tax_number: FormControl<string | null>;
  client_data: FormGroup<ClientDataControls>;
  party_type_id: FormControl<number | null>;
  email: FormControl<string | null>;
  finance_email: FormControl<string | null>;
  phone: FormControl<string | null>;
  lock_representatives: FormControl<boolean>;
  representatives: FormArray<FormGroup<RepresentativeControls>>;
};

export type ClaimFileControls = {
  id: FormControl<string | null>;
  file_name: FormControl<string | null>;
  storage_url: FormControl<string | File | null>;
  invoice_number: FormControl<string | null>;
  invoice_date: FormControl<string | Date | null>;
  case_type: FormControl<CaseType | null>;
};

export type InterestClaimControls = {
  remaining_amount: FormControl<number | null>;
  original_amount: FormControl<number | null>;
  interest_rate: FormControl<string | null>;
};

export type ClaimControls = {
  id: FormControl<string | null>;
  files: FormArray<FormGroup<ClaimFileControls>>;
  due_date_at: FormControl<string | Date | null>;
  remaining_amount: FormControl<number | null>;
  original_amount: FormControl<number | null>;
  currency_iso: FormControl<string | null>;
  type_id: FormControl<number | null>;
  specification: FormControl<string | null>;
  ref_id: FormControl<string | null>;
  interest_claim: FormGroup<InterestClaimControls>;
};

export type DebtorDataControls = {
  birth_date: FormControl<string | Date | null>;
  birth_place: FormControl<string | null>;
  mother_name: FormControl<string | null>;
  birth_name: FormControl<string | null>;
  personal_id: FormControl<string | null>;
  registration_number: FormControl<string | null>;
  employer_name: FormControl<string | null>;
};

export type DebtorControls = {
  id: FormControl<string | null>;
  company_updated_at: FormControl<string | null>;
  name: FormControl<string | null>;
  bank_account: FormGroup<BankAccountControls>;
  address: FormGroup<AddressControls>;
  tax_number: FormControl<string | null>;
  debtor_data: FormGroup<DebtorDataControls>;
  party_type_id: FormControl<number | null>;
  email: FormControl<string | null>;
  phone: FormControl<string | null>;
  lock_representatives: FormControl<boolean>;
  representatives: FormArray<FormGroup<RepresentativeControls>>;
};

export type LegalRelationshipControls = {
  files: FormArray<FormControl<LegalRelationshipFile>>;
  specification: FormControl<string | null>;
  description: FormControl<string | null>;
  type_id: FormControl<number | null>;
  law_firms: FormArray<FormControl<LawFirmItem>>;
  external_ids: FormArray<FormGroup<ExternalIdControls>>;
};

export type CaseControls = {
  legal_relationship: FormGroup<LegalRelationshipControls>;
  client: FormGroup<ClientControls>;
  claims: FormArray<FormGroup<ClaimControls>>;
  debtors: FormArray<FormGroup<DebtorControls>>;
  interest_type: FormControl<InterestType | null>;
};

@Injectable({
  providedIn: 'root',
})
export class EditCaseDialogService {
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private casesService = inject(CasesService);
  private snackbar = inject(MatSnackBar);
  private enumsService = inject(EnumsService);
  private countriesService = inject(CountriesService);
  private companySearchService = inject(CompanySearchService);

  readonly form: FormGroup<CaseControls>;
  data?: GetEditCaseResponse;
  caseId?: string;
  readonly invoiceItemPreview = signal<string | null>(null);

  partyTypes: PartyType[] = [];
  representativeTypes: RepresentativeType[] = [];
  registrationOffices: RegistrationOffice[] = [];
  countries: Country[] = [];

  private referenceDataPromise?: Promise<void>;

  globalCaseType: CaseType = 'invoice';
  claimsGlobalControls?: ClaimsGlobalControls;
  claimTypes: ClaimType[] = [];

  private readonly aiFilledControls = new Set<AbstractControl>();
  private readonly aiFilledClearSubs = new Map<AbstractControl, Subscription>();

  constructor() {
    this.form = this.fb.group<CaseControls>({
      legal_relationship: this.fb.group<LegalRelationshipControls>({
        files: this.fb.array<FormControl<LegalRelationshipFile>>([]),
        specification: this.fb.control<string | null>(null, Validators.maxLength(255)),
        description: this.fb.control<string | null>(null, Validators.maxLength(255)),
        type_id: this.fb.control<number | null>(null),
        law_firms: this.fb.array<FormControl<LawFirmItem>>([]),
        external_ids: this.fb.array<FormGroup<ExternalIdControls>>([]),
      }),
      client: this.fb.group<ClientControls>({
        company_updated_at: this.fb.control<string | null>(null),
        name: this.fb.control<string | null>(null, Validators.maxLength(255)),
        tax_number: this.fb.control<string | null>(null, Validators.maxLength(255)),
        bank_account: this.getBankAccountForm(),
        address: this.getAddressForm(),
        client_data: this.fb.group<ClientDataControls>({
          birth_date: this.fb.control<Date | string | null>(null),
          birth_place: this.fb.control<string | null>(null, Validators.maxLength(255)),
          mother_name: this.fb.control<string | null>(null, Validators.maxLength(255)),
          registration_number: this.fb.control<string | null>(null, Validators.maxLength(255)),
        }),
        party_type_id: this.fb.control<number | null>(null),
        email: this.fb.control<string | null>(null, Validators.email),
        finance_email: this.fb.control<string | null>(null, Validators.email),
        phone: this.fb.control<string | null>(null),
        lock_representatives: this.fb.control(false, { nonNullable: true }),
        representatives: this.fb.array<FormGroup<RepresentativeControls>>([], Validators.maxLength(2)),
      }),
      claims: this.fb.array<FormGroup<ClaimControls>>([]),
      debtors: this.fb.array<FormGroup<DebtorControls>>([]),
      interest_type: this.fb.control<InterestType | null>(null),
    });
  }

  upsertAddressForm(form: FormGroup<AddressControls>, address?: Address | null): void {
    if (address) {
      form.enable();
      form.patchValue(address);
    } else {
      form.reset();
      form.disable();
    }
  }

  async populateRepresentativesFromTaxNumber(
    representatives: FormArray<FormGroup<RepresentativeControls>>,
    taxNumber: string | null | undefined,
    disableDuringLoad?: AbstractControl | null,
  ): Promise<void> {
    if (!taxNumber) {
      return;
    }

    representatives.clear();
    disableDuringLoad?.disable();

    try {
      const result = await this.companySearchService.getRepresentatives(taxNumber);
      if (!result) {
        return;
      }

      for (const rep of result.representatives) {
        const formGroup = this.addRepresentative(representatives);
        let registrationNumber =
          rep.representative_data?.registration_number ?? null;
        let registrationOfficeId = this.registrationOfficeIdFromSearchRep(rep);

        const representativeType = this.representativeTypes.find(
          (type) => type.id === rep.representativeType?.id,
        );
        if (
          this.isRepresentativeTypeInd(representativeType)
          && rep.tax_number
          && (!registrationNumber || registrationOfficeId == null)
        ) {
          const company = await this.companySearchService.exactSearch(rep.tax_number);
          registrationNumber ??= company?.registration_number ?? null;
          registrationOfficeId ??= company?.registration_office_id ?? null;
        }

        formGroup.patchValue({
          name: rep.name,
          tax_number: rep.tax_number,
          type_id: rep.representativeType?.id ?? null,
          party_type_id: rep.partyType?.id ?? null,
          registration_number: registrationNumber,
          registration_office_id: registrationOfficeId,
        });
        this.upsertAddressForm(formGroup.controls.address, rep.address);

        await this.populateRepresentativesFromTaxNumber(
          formGroup.controls.representatives,
          rep.tax_number,
          formGroup,
        );
      }
    } catch (error) {
      console.error('Error while getting representatives', error);
    } finally {
      disableDuringLoad?.enable();
    }
  }

  async applyCompanySearchToParty(
    form: FormGroup<ClientControls> | FormGroup<DebtorControls>,
    item: CompanySearchAutocompleteItem,
    registrationNumberControl?: FormControl<string | null>,
  ): Promise<void> {
    form.patchValue({
      name: item.name,
      tax_number: item.tax_number,
      party_type_id: item.party_type?.id ?? null,
    });
    this.upsertAddressForm(form.controls.address, {
      country_id: item.country?.id ?? null,
      postcode: item.postcode,
      settlement: item.settlement,
      street: item.street,
    });

    try {
      const detailed = await this.companySearchService.detailedSearch(
        this.taxNumberRoot(item.tax_number),
      );
      if (detailed?.company) {
        if (registrationNumberControl && detailed.company.registration_number) {
          registrationNumberControl.patchValue(detailed.company.registration_number);
        }
        if (!item.party_type?.id && detailed.company.partyType?.id) {
          form.controls.party_type_id.patchValue(detailed.company.partyType.id);
        }
      }
    } catch (error) {
      console.error('Error while fetching company details', error);
    }

    const partyType = item.party_type
      ?? this.partyTypes.find((p) => p.id === form.value.party_type_id);
    if (partyType?.type?.toLowerCase() === 'ind' || partyType?.internal_id === '91-a') {
      return;
    }

    const representatives = form.controls.representatives;
    const shouldDisableAfter = !form.value.lock_representatives;

    if (representatives.disabled) {
      representatives.enable({ emitEvent: false });
    }

    await this.populateRepresentativesFromTaxNumber(
      representatives,
      item.tax_number,
    );

    if (shouldDisableAfter) {
      representatives.disable({ emitEvent: false });
    }
  }

  async applyCompanySearchToRepresentative(
    form: FormGroup<RepresentativeControls>,
    item: CompanySearchAutocompleteItem,
  ): Promise<void> {
    form.patchValue({
      name: item.name,
      tax_number: item.tax_number,
      type_id: null,
      party_type_id: item.party_type?.id ?? null,
      registration_number: item.registration_number,
      registration_office_id: item.registration_office_id,
    });
    this.upsertAddressForm(form.controls.address, {
      country_id: item.country?.id ?? null,
      postcode: item.postcode,
      settlement: item.settlement,
      street: item.street,
    });

    await this.populateRepresentativesFromTaxNumber(
      form.controls.representatives,
      item.tax_number,
      form,
    );
  }

  private taxNumberRoot(taxNumber: string | null | undefined): string {
    return (taxNumber ?? '').replace(/\D/g, '').slice(0, 8);
  }

  private registrationOfficeIdFromSearchRep(
    rep: NonNullable<
      Awaited<ReturnType<CompanySearchService['getRepresentatives']>>
    >['representatives'][number],
  ): number | null {
    const office = this.registrationOffices.find(
      (item) => item.internal_id === rep.registrationOffice?.internal_id
        || item.name === rep.registrationOffice?.name,
    );

    if (office) {
      return Number(office.id);
    }

    return rep.representativeType?.registration_office_id ?? null;
  }

  private isRepresentativeTypeInd(
    representativeType: RepresentativeType | undefined,
  ): boolean {
    return representativeType?.type?.toLowerCase() === 'ind';
  }

  upsertBankAccountForm(
    form: FormGroup<BankAccountControls>,
    bankAccount?: Partial<BankAccount> | null,
    lock_bank_account = false,
  ): void {
    if (bankAccount) {
      form.enable();
      form.patchValue({
        ...bankAccount,
        lock_bank_account,
        account_number: bankAccount.iban || bankAccount.account_number,
      });
    } else {
      form.reset();
    }

    if (!lock_bank_account) {
      form.disable();
    }
  }

  addRepresentatives(form: FormArray<FormGroup<RepresentativeControls>>, representatives: Representative[]): void {
    const repForms: Record<number, FormGroup<RepresentativeControls>> = {};
    const remainingReps = representatives.concat();
    while (remainingReps.length > 0) {
      for (let i = remainingReps.length - 1; i >= 0; --i) {
        const rep = remainingReps[i];
        if (
          rep.parent_id
          && !(rep.parent_id in repForms)
        ) {
          continue;
        }

        if (rep.parent_id) {
          const parent = repForms[rep.parent_id];
          const repForm = this.addRepresentative(
            parent.controls.representatives,
            rep,
          );
          repForms[rep.id] = repForm;
        } else {
          const repForm = this.addRepresentative(
            form,
            rep,
          );
          repForms[rep.id] = repForm;
        }
        remainingReps.splice(i, 1);
      }
    }
  }

  addRepresentative(
    representatives: FormArray<FormGroup<RepresentativeControls>>,
    rep?: Representative,
  ): FormGroup<RepresentativeControls> {
    const address = this.getAddressForm();
    const form = this.fb.group<RepresentativeControls>({
      id: this.fb.control(rep?.id ?? null),
      company_updated_at: this.fb.control(rep?.company_updated_at ?? null),
      is_standalone: this.fb.control(rep?.is_standalone ?? true, {
        nonNullable: true,
      }),
      name: this.fb.control(rep?.name ?? null, Validators.maxLength(255)),
      tax_number: this.fb.control(rep?.tax_number ?? null, Validators.maxLength(255)),
      party_type_id: this.fb.control(rep?.party_type_id ?? null),
      registration_number: this.fb.control(rep?.registration_number ?? null, Validators.maxLength(255)),
      registration_office_id: this.fb.control(rep?.registration_office_id ?? null),
      type_id: this.fb.control(rep?.representative_type_id ?? null),
      address,
      representatives: this.fb.array<FormGroup<RepresentativeControls>>([], Validators.maxLength(2)),
    });

    this.upsertAddressForm(address, rep?.address);

    representatives.push(form);

    return form;
  };

  addLawFirm(lawFirm: LawFirmItem): FormControl<LawFirmItem> {
    const lawFirms = this.form.controls.legal_relationship.controls.law_firms;
    const form = this.fb.control(lawFirm, { nonNullable: true });
    lawFirms.push(form);
    return form;
  }

  addClaim(claim?: EditClaim): FormGroup<ClaimControls> {
    const files = this.fb.array<FormGroup<ClaimFileControls>>([]);
    const interest_claim = this.fb.group<InterestClaimControls>({
      remaining_amount: this.fb.control(claim?.interest_claim?.remaining_amount ?? null, Validators.required),
      original_amount: this.fb.control(claim?.interest_claim?.original_amount ?? null, Validators.required),
      interest_rate: this.fb.control(claim?.interest_claim?.rate ?? null),
    });

    const form = this.fb.group<ClaimControls>({
      id: this.fb.control(claim?.id ?? null),
      files,
      due_date_at: this.fb.control<string | Date | null>(claim?.due_date_at ?? null),
      remaining_amount: this.fb.control(claim?.remaining_amount ?? null, Validators.required),
      original_amount: this.fb.control(claim?.original_amount ?? null, Validators.required),
      currency_iso: this.fb.control(claim?.currency_iso ?? null, Validators.required),
      type_id: this.fb.control(claim?.claim_type_id ?? null),
      specification: this.fb.control(claim?.claim_type_specification ?? null),
      interest_claim,
      ref_id: this.fb.control(claim?.ref_id ?? null, Validators.required),
    });

    if (!this.data?.payee_case.is_szamlazzhu) {
      form.controls.ref_id.disable();
    }

    if (claim) {
      for (const file of claim.files) {
        this.addClaimFile(files, file);
      }
    }

    if (this.data?.interest_type !== 'contractual') {
      interest_claim.disable();
    }

    const claims = this.form.controls.claims;
    claims.push(form);

    return form;
  }

  addClaimFile(
    filesForm: FormArray<FormGroup<ClaimFileControls>>,
    file: File | UploadedFile,
  ): FormGroup<ClaimFileControls> {
    const fileForm = this.fb.group<ClaimFileControls>({
      id: this.fb.control<string | null>(null),
      file_name: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      storage_url: this.fb.control<string | File | null>(null, [Validators.required, Validators.maxLength(255)]),
      invoice_number: this.fb.control<string | null>(null, Validators.maxLength(255)),
      invoice_date: this.fb.control<string | Date | null>(null),
      case_type: this.fb.control<CaseType | null>(this.globalCaseType),
    });

    if (file instanceof File) {
      fileForm.patchValue({
        file_name: file.name,
        storage_url: file,
      });
    } else {
      fileForm.patchValue(file);
    }

    filesForm.push(fileForm);

    return fileForm;
  }

  async getInvoiceOcr(file: File): Promise<InvoiceOcrData> {
    const url = `${environment.baseUrl}/api/invoice-ocr`;
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post<InvoiceOcrData>(url, formData);
    return response.data;
  }

  async getLegalRelationshipOcr(file: File): Promise<LegalRelationshipOcrData> {
    const url = `${environment.baseUrl}/api/legal-relationship-ocr`;
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post<LegalRelationshipOcrData>(url, formData);
    return response.data;
  }

  async getLegalRelationshipOcrByMedia(
    mediaUuid: string,
  ): Promise<LegalRelationshipOcrData> {
    const url = `${environment.baseUrl}/api/legal-relationship-ocr`;
    const formData = new FormData();
    formData.append('media_uuid', mediaUuid);
    const response = await axios.post<LegalRelationshipOcrData>(url, formData);
    return response.data;
  }

  async getCondominiumAccountStatementOcr(
    file: File,
  ): Promise<CondominiumAccountStatementOcrData> {
    const url = `${environment.baseUrl}/api/condominium-account-statement-ocr`;
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post<CondominiumAccountStatementOcrData>(
      url,
      formData,
    );
    return response.data;
  }

  async getCondominiumAccountStatementOcrByMedia(
    mediaUuid: string,
  ): Promise<CondominiumAccountStatementOcrData> {
    const url = `${environment.baseUrl}/api/condominium-account-statement-ocr`;
    const formData = new FormData();
    formData.append('media_uuid', mediaUuid);
    const response = await axios.post<CondominiumAccountStatementOcrData>(
      url,
      formData,
    );
    return response.data;
  }

  isCondominiumCommonCostClaimType(
    typeId: number | null | undefined,
    claimTypes: ClaimType[],
  ): boolean {
    if (!typeId) {
      return false;
    }

    return (
      claimTypes.find((ct) => ct.id === typeId)?.internal_id ===
      CONDOMINIUM_COMMON_COST_CLAIM_TYPE_INTERNAL_ID
    );
  }

  applyLegalRelationshipOcr(
    data: LegalRelationshipOcrData,
    options?: {
      claimForm?: FormGroup<ClaimControls>;
      fileForm?: FormGroup<ClaimFileControls>;
    },
  ): void {
    const legalRelationshipForm = this.form.controls.legal_relationship;

    if (
      data.legal_relationship_id &&
      !legalRelationshipForm.controls.type_id.value
    ) {
      this.setAiValue(
        legalRelationshipForm.controls.type_id,
        data.legal_relationship_id,
      );
    }

    const preview = this.formatInvoiceItem(data.invoice_item);
    if (preview) {
      this.invoiceItemPreview.set(preview);
    }

    this.applyLegalRelationshipContractOcr(data, options);
  }

  private applyLegalRelationshipContractOcr(
    data: LegalRelationshipOcrData,
    options?: {
      claimForm?: FormGroup<ClaimControls>;
      fileForm?: FormGroup<ClaimFileControls>;
    },
  ): void {
    const caseType = this.resolveOcrCaseType(data.case_type);
    if (caseType) {
      this.globalCaseType = caseType;
      if (this.claimsGlobalControls) {
        this.setAiValue(this.claimsGlobalControls.caseType, caseType);
      }
      this.forClaimFiles(options?.fileForm, (fileForm) => {
        this.setAiValue(fileForm.controls.case_type, caseType);
      });
    }

    const identification =
      data.claim_identification_text ?? data.contract_identifier ?? null;
    if (identification) {
      if (this.claimsGlobalControls) {
        this.setAiValue(
          this.claimsGlobalControls.contractIdentification,
          identification,
        );
      }
      this.forClaimFiles(options?.fileForm, (fileForm) => {
        this.setAiValue(fileForm.controls.invoice_number, identification);
      });
    }

    if (data.contract_date_at) {
      const contractDate = parseISO(data.contract_date_at);
      if (isValid(contractDate)) {
        if (this.claimsGlobalControls) {
          this.setAiValue(this.claimsGlobalControls.contractDate, contractDate);
        }
        this.forClaimFiles(options?.fileForm, (fileForm) => {
          this.setAiValue(fileForm.controls.invoice_date, contractDate);
        });
      }
    }

    const claimType = data.claim_type_internal_id
      ? this.claimTypes.find(
          (ct) => ct.internal_id === data.claim_type_internal_id,
        )
      : undefined;
    if (!claimType) {
      return;
    }

    const claimForms = options?.claimForm
      ? [options.claimForm]
      : this.form.controls.claims.controls;
    for (const claimForm of claimForms) {
      if (!claimForm.controls.type_id.value) {
        this.setAiValue(claimForm.controls.type_id, claimType.id);
      }
    }
    if (
      this.claimsGlobalControls &&
      !this.claimsGlobalControls.claimType.value
    ) {
      this.setAiValue(this.claimsGlobalControls.claimType, claimType.id);
    }
  }

  private resolveOcrCaseType(caseType: string | null | undefined): CaseType | null {
    if (
      caseType === CASE_TYPE.CONTRACT ||
      caseType === CASE_TYPE.INVOICE ||
      caseType === CASE_TYPE.CONDOMINIUM
    ) {
      return caseType;
    }

    return null;
  }

  private forClaimFiles(
    fileForm: FormGroup<ClaimFileControls> | undefined,
    apply: (fileForm: FormGroup<ClaimFileControls>) => void,
  ): void {
    if (fileForm) {
      apply(fileForm);
      return;
    }

    this.form.controls.claims.controls.forEach((claim) => {
      claim.controls.files.controls.forEach((file) => apply(file));
    });
  }

  private formatInvoiceItem(
    item: LegalRelationshipOcrInvoiceItem | null | undefined,
  ): string | null {
    if (!item) {
      return null;
    }

    const parts = [item.name, item.description]
      .map((value) => value?.trim())
      .filter((value): value is string => !!value);

    if (parts.length === 0) {
      return null;
    }

    return parts.join('\n');
  }

  applyInvoiceOcrToClaim(
    claimForm: FormGroup<ClaimControls>,
    fileForm: FormGroup<ClaimFileControls>,
    data: InvoiceOcrData,
    claimTypes: ClaimType[] = [],
  ): void {
    if (data.invoice_number != null) {
      this.setAiValue(fileForm.controls.invoice_number, data.invoice_number);
    }
    if (data.invoice_issued_at) {
      this.setAiValue(fileForm.controls.invoice_date, parseISO(data.invoice_issued_at));
    }

    const amount = data.claim_original_amount ?? null;
    const caseType =
      fileForm.controls.case_type.value ??
      this.globalCaseType;
    if (amount != null && caseType !== CASE_TYPE.CONTRACT) {
      this.setAiValue(claimForm.controls.original_amount, amount);
      this.setAiValue(claimForm.controls.remaining_amount, amount);
    }
    if (data.currency_iso != null) {
      this.setAiValue(claimForm.controls.currency_iso, data.currency_iso);
    }
    if (data.claim_due_date_at) {
      this.setAiValue(claimForm.controls.due_date_at, parseISO(data.claim_due_date_at));
    }

    const claimType = data.claim_type_internal_id
      ? claimTypes.find(ct => ct.internal_id === data.claim_type_internal_id)
      : undefined;
    if (claimType && !claimForm.controls.type_id.value) {
      this.setAiValue(claimForm.controls.type_id, claimType.id);
      if (
        this.claimsGlobalControls &&
        !this.claimsGlobalControls.claimType.value
      ) {
        this.setAiValue(this.claimsGlobalControls.claimType, claimType.id);
      }
    }
  }

  applyCondominiumAccountStatementOcr(
    fileForm: FormGroup<ClaimFileControls>,
    globalCaseType: FormControl<CaseType>,
    data: CondominiumAccountStatementOcrData,
  ): void {
    this.globalCaseType = CASE_TYPE.CONDOMINIUM;
    this.setAiValue(globalCaseType, CASE_TYPE.CONDOMINIUM);
    this.setAiValue(fileForm.controls.case_type, CASE_TYPE.CONDOMINIUM);
    if (data.unit_address) {
      this.setAiValue(fileForm.controls.invoice_number, data.unit_address);
    }
    if (data.debt_start_at) {
      this.setAiValue(fileForm.controls.invoice_date, parseISO(data.debt_start_at));
    }

    this.applyCommonRepresentativeFromOcr(data);
  }

  private applyCommonRepresentativeFromOcr(
    data: CondominiumAccountStatementOcrData,
  ): void {
    if (!data.common_representative_name) {
      return;
    }

    const client = this.form.controls.client;
    const representatives = client.controls.representatives;
    const hasExistingRepresentative = representatives.controls.some(
      (rep) => !!rep.value.name || !!rep.value.id,
    );
    if (hasExistingRepresentative) {
      return;
    }

    representatives.clear();
    const hungaryCountryId =
      this.countries.find((country) => country.iso === 'HU')?.id ?? null;
    const hasAddress = !!(
      data.common_representative_postcode ||
      data.common_representative_city ||
      data.common_representative_street
    );

    const representativeForm = this.addRepresentative(representatives);
    this.setAiValue(representativeForm.controls.name, data.common_representative_name);
    if (data.common_representative_type_id != null) {
      this.setAiValue(
        representativeForm.controls.type_id,
        data.common_representative_type_id,
      );
    }
    this.upsertAddressForm(
      representativeForm.controls.address,
      hasAddress
        ? {
            country_id: hungaryCountryId,
            postcode: data.common_representative_postcode,
            settlement: data.common_representative_city,
            street: data.common_representative_street,
          }
        : null,
    );
    if (hasAddress) {
      const address = representativeForm.controls.address;
      if (hungaryCountryId != null) {
        this.markAiFilled(address.controls.country_id);
      }
      if (data.common_representative_postcode) {
        this.markAiFilled(address.controls.postcode);
      }
      if (data.common_representative_city) {
        this.markAiFilled(address.controls.settlement);
      }
      if (data.common_representative_street) {
        this.markAiFilled(address.controls.street);
      }
    }

    client.controls.lock_representatives.patchValue(true);
    representatives.enable();
  }

  isAiFilled(control: AbstractControl | null | undefined): boolean {
    return !!control && this.aiFilledControls.has(control);
  }

  hasAiFilledIn(root: AbstractControl | null | undefined): boolean {
    if (!root) {
      return false;
    }

    const visit = (control: AbstractControl): boolean => {
      if (this.aiFilledControls.has(control)) {
        return true;
      }
      if (control instanceof FormGroup) {
        return Object.values(control.controls).some(visit);
      }
      if (control instanceof FormArray) {
        return control.controls.some(visit);
      }
      return false;
    };

    return visit(root);
  }

  private hasControlValue(control: AbstractControl): boolean {
    const value = control.value;
    if (!value) {
      return false;
    }
    if (value instanceof Date) {
      return isValid(value);
    }
    if (typeof value === 'number') {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return true;
  }

  private setAiValue<T>(control: AbstractControl<T>, value: T): void {
    if (this.hasControlValue(control)) {
      return;
    }
    control.setValue(value);
    this.markAiFilled(control);
  }

  private markAiFilled(control: AbstractControl): void {
    this.aiFilledControls.add(control);
    this.aiFilledClearSubs.get(control)?.unsubscribe();
    const sub = control.valueChanges.pipe(take(1)).subscribe(() => {
      this.clearAiFilled(control);
    });
    this.aiFilledClearSubs.set(control, sub);
  }

  private clearAiFilled(control: AbstractControl): void {
    if (!this.aiFilledControls.delete(control)) {
      return;
    }
    this.aiFilledClearSubs.get(control)?.unsubscribe();
    this.aiFilledClearSubs.delete(control);
  }

  addDebtor(debtor?: EditDebtor): FormGroup<DebtorControls> {
    const address = this.getAddressForm();
    const bank_account = this.getBankAccountForm();
    const representatives = this.fb.array<FormGroup<RepresentativeControls>>([], Validators.maxLength(2));

    const form = this.fb.group<DebtorControls>({
      id: this.fb.control(debtor?.id ?? null),
      company_updated_at: this.fb.control(debtor?.company_updated_at ?? null),
      name: this.fb.control(debtor?.name ?? null, Validators.maxLength(255)),
      bank_account,
      address,
      tax_number: this.fb.control(debtor?.tax_number ?? null, Validators.maxLength(255)),
      debtor_data: this.fb.group<DebtorDataControls>({
        birth_date: this.fb.control<string | Date | null>(debtor?.birth_date ?? null),
        birth_place: this.fb.control(debtor?.birth_place ?? null, Validators.maxLength(255)),
        mother_name: this.fb.control(debtor?.mother_name ?? null, Validators.maxLength(255)),
        birth_name: this.fb.control(debtor?.birth_name ?? null, Validators.maxLength(255)),
        personal_id: this.fb.control(debtor?.personal_id ?? null, Validators.maxLength(255)),
        registration_number: this.fb.control(debtor?.registration_number ?? null, Validators.maxLength(255)),
        employer_name: this.fb.control(debtor?.employer_name ?? null, Validators.maxLength(255)),
      }),
      party_type_id: this.fb.control(debtor?.party_type_id ?? null),
      email: this.fb.control(debtor?.email ?? null),
      phone: this.fb.control(debtor?.phone ?? null),
      lock_representatives: this.fb.control(!!debtor?.lock_representatives, { nonNullable: true }),
      representatives,
    });

    this.upsertAddressForm(
      address,
      debtor?.address,
    );

    this.upsertBankAccountForm(
      bank_account,
      debtor?.bank_account,
      debtor?.lock_bank_account,
    );

    if (debtor?.representatives) {
      this.addRepresentatives(
        representatives,
        debtor.representatives,
      );
    }
    if (debtor?.lock_representatives === false) {
      representatives.disable();
    }

    const debtors = this.form.controls.debtors;
    debtors.push(form);

    return form;
  }

  resetLegalRelationship(): void {
    if (!this.data) {
      return;
    }

    this.form.patchValue({
      legal_relationship: {
        type_id: this.data.payee_case.legal_relationship_id,
        description: this.data.payee_case.description,
        specification: this.data.payee_case.legal_relationship_specification,
      },
    });

    const files = this.form.controls.legal_relationship.controls.files;
    files.clear();
    for (const file of this.data.payee_case.legal_relationship_files) {
      const fileValue: LegalRelationshipFile = file;
      files.push(this.fb.control(fileValue, { nonNullable: true }));
    }

    const lawFirms = this.form.controls.legal_relationship.controls.law_firms;
    lawFirms.clear();
    for (const lawFirm of this.data.law_firms) {
      const lawFirmValue: LawFirmItem = lawFirm;
      lawFirms.push(this.fb.control(lawFirmValue, { nonNullable: true }));
    }

    const externalIds = this.form.controls.legal_relationship.controls.external_ids;
    externalIds.clear();
    for (const externalId of this.data.payee_case.external_ids) {
      externalIds.push(this.fb.group<ExternalIdControls>({
        type: this.fb.control(externalId.type, { nonNullable: true }),
        value: this.fb.control(externalId.value, { nonNullable: true }),
      }));
    }
  }

  resetClient(): void {
    if (!this.data) {
      return;
    }

    const client = this.data.client;
    this.form.patchValue({
      client: {
        name: client.name,
        tax_number: client.tax_number,
        party_type_id: client.party_type_id,
        email: client.email,
        phone: client.phone,
        finance_email: client.finance_email,
        client_data: client.client_data,
        company_updated_at: client.company_updated_at,
        lock_representatives: !!client.lock_representatives,
      },
    });

    this.upsertBankAccountForm(
      this.form.controls.client.controls.bank_account,
      client.bank_account,
      client.lock_bank_account,
    );

    this.upsertAddressForm(
      this.form.controls.client.controls.address,
      client.address,
    );

    const representatives = this.form.controls.client.controls.representatives;
    representatives.clear();
    this.addRepresentatives(
      representatives,
      client.representatives,
    );
    if (client?.lock_representatives === false) {
      representatives.disable();
    }
  }

  loadReferenceData(): Promise<void> {
    if (!this.referenceDataPromise) {
      this.referenceDataPromise = Promise.allSettled([
        this.enumsService
          .getEnum<PartyType>(partyType.model)
          .then((v) => (this.partyTypes = v)),
        this.enumsService
          .getEnum<RepresentativeType>(representativeType.model)
          .then((v) => (this.representativeTypes = v)),
        this.enumsService
          .getEnum<ClaimType>(claimType.model)
          .then((v) => (this.claimTypes = v)),
        this.enumsService
          .getEnum<RegistrationOffice>(registrationOffice.model)
          .then((v) => (this.registrationOffices = v)),
        this.countriesService
          .getCountries()
          .then((v) => (this.countries = v.countries)),
      ]).then(() => undefined);
    }

    return this.referenceDataPromise;
  }

  async setForm(caseId: string): Promise<void> {
    this.caseId = caseId;
    this.data = await this.casesService.getEditCaseData(caseId);
    this.resetForm();
  }

  resetForm(): void {
    if (!this.data) {
      return;
    }

    this.invoiceItemPreview.set(null);
    this.resetLegalRelationship();
    this.resetClient();
    this.form.controls.interest_type.patchValue(this.data.interest_type);

    const claims = this.form.controls.claims;
    claims.clear();
    for (const claim of this.data.claims) {
      this.addClaim(claim);
    }

    const debtors = this.form.controls.debtors;
    debtors.clear();
    for (const debtor of this.data.debtors) {
      this.addDebtor(debtor);
    }
    if (this.data.debtors.length === 0) {
      this.addDebtor();
    }
  }

  async submit(): Promise<GetEditCaseResponse> {
    if (this.form.invalid || !this.caseId) {
      throw new Error('Invalid form');
    }

    try {
      const value = this.form.getRawValue();

      const response = await this.casesService.updateCase(
        this.caseId,
        {
          legal_relationship: {
            specification: value.legal_relationship.specification,
            type_id: value.legal_relationship.type_id,
            description: value.legal_relationship.description,
            law_firms: value.legal_relationship.law_firms.map(lf => lf.id),
            external_ids: value.legal_relationship.external_ids.filter(
              (externalId) => !!externalId.value?.trim(),
            ),
            files: value.legal_relationship.files.map(f => f.storage_url instanceof File
              ? {
                file: f.storage_url,
                id: null,
              }
              : {
                file: null,
                id: f.id,
              },
            ),
          },
          client: {
            party_type_id: value.client.party_type_id,
            address: value.client.address,
            bank_account: value.client.bank_account?.account_number
              ? {
                account_number: this.getBBAN(value.client.bank_account.account_number),
                iban: this.getIBAN(value.client.bank_account.account_number),
                bic: value.client.bank_account.bic,
                bank_name: value.client.bank_account.bank_name,
              }
              : null,
            client_data: {
              birth_date: this.formatDate(value.client.client_data.birth_date),
              birth_place: value.client.client_data.birth_place,
              mother_name: value.client.client_data.mother_name,
              registration_number: value.client.client_data.registration_number,
            },
            name: value.client.name ?? '',
            email: value.client.email,
            finance_email: value.client.finance_email,
            phone: value.client.phone,
            tax_number: value.client.tax_number,
            lock_representatives: value.client.lock_representatives,
            lock_bank_account: value.client.bank_account?.lock_bank_account ?? false,
            representatives: this.mapRepresentativesToUpdate(value.client.representatives) ?? undefined,
          },
          claims: value.claims.map<UpdateCaseParams['claims'][number]>(claim => {
            const case_type = claim.files[0]?.case_type;

            return {
              id: claim.id,
              currency_iso: claim.currency_iso ?? 'HUF',
              files: claim.files.map(f => {
                if (f.storage_url instanceof File) {
                  return {
                    file: f.storage_url,
                    id: null,
                    invoice_date: this.formatDate(f.invoice_date),
                    invoice_number: f.invoice_number,
                  };
                } else {
                  return {
                    file: null,
                    id: f.id,
                    invoice_date: this.formatDate(f.invoice_date),
                    invoice_number: f.invoice_number,
                  };
                }
              }),
              ref_id: this.data?.payee_case.is_szamlazzhu
                ? claim.ref_id || undefined
                : undefined,
              original_amount: claim.original_amount ?? 0,
              remaining_amount: claim.remaining_amount ?? 0,
              case_type,
              type_id: claim.type_id,
              specification: claim.specification,
              due_date_at: this.formatDate(claim.due_date_at),
              interest_type: value.interest_type,
              interest_claim: value.interest_type === 'contractual' && claim.interest_claim
                ? {
                  interest_rate: claim.interest_claim.interest_rate,
                  remaining_amount: claim.interest_claim.remaining_amount ?? 0,
                  original_amount: claim.interest_claim.original_amount ?? 0,
                }
                : undefined,
            };
          }),
          debtors: value.debtors.map<UpdateCaseParams['debtors'][number]>(debtor => {
            return {
              address: debtor.address,
              id: debtor.id,
              bank_account: debtor.bank_account?.account_number
                ? {
                  account_number: this.getBBAN(debtor.bank_account.account_number),
                  iban: this.getIBAN(debtor.bank_account.account_number),
                  bic: debtor.bank_account.bic,
                  bank_name: debtor.bank_account.bank_name,
                }
                : null,
              debtor_data: {
                birth_date: this.formatDate(debtor.debtor_data.birth_date),
                birth_name: debtor.debtor_data.birth_name,
                birth_place: debtor.debtor_data.birth_place,
                employer_name: debtor.debtor_data.employer_name,
                mother_name: debtor.debtor_data.mother_name,
                personal_id: debtor.debtor_data.personal_id,
                registration_number: debtor.debtor_data.registration_number,
              },
              name: debtor.name,
              tax_number: debtor.tax_number,
              party_type_id: debtor.party_type_id,
              email: debtor.email,
              phone: debtor.phone,
              lock_representatives: debtor.lock_representatives,
              lock_bank_account: debtor.bank_account?.lock_bank_account ?? false,
              representatives: this.mapRepresentativesToUpdate(debtor.representatives) ?? undefined,
            };
          }),
          interest_type: value.interest_type,
        },
      );
      this.snackbar.open('Sikeres mentés', 'OK', {
        duration: 2500,
      });

      return response;
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error while updating case', error.response?.data);
        this.snackbar.open(
          error.response?.data?.message || 'Valami hiba történt mentés közben!',
          'OK',
          {
            duration: 5000,
          },
        );
      } else {
        console.error('Unknown error while updating case', error);
        this.snackbar.open(
          'Valami hiba történt mentés közben!',
          'OK',
          {
            duration: 5000,
          },
        );
      }
      this.helpersService.markAllChildrenAsDirty(this.form);
      throw error;
    }
  }

  private getBBAN(number: unknown): null | string {
    if (typeof number !== 'string') {
      return null;
    }

    const formatted = electronicFormatIBAN(number);
    if (formatted && isValidIBAN(formatted)) {
      return null;
    } else {
      return formatted;
    }
  }

  private getIBAN(number: unknown): null | string {
    if (typeof number !== 'string') {
      return null;
    }

    const formatted = electronicFormatIBAN(number);
    if (formatted && isValidIBAN(formatted)) {
      return formatted;
    } else {
      return null;
    }
  }

  private formatDate(date: Date | string | null | undefined): string | null {
    if (date == null || date === '') {
      return null;
    }

    const parsed = date instanceof Date ? date : parseISO(date);
    if (!isValid(parsed)) {
      return null;
    }

    return format(parsed, 'yyyy-MM-dd');
  }

  private mapRepresentativesToUpdate(
    forms?: ReturnType<FormGroup<RepresentativeControls>['getRawValue']>[],
  ): null | UpdateRepresentative[] {
    if (!forms) {
      return null;
    }

    return forms.map(r => ({
      address: r.address
        ? {
          country_id: r.address?.country_id,
          postcode: r.address?.postcode,
          settlement: r.address?.settlement,
          street: r.address?.street,
        }
        : null,
      representatives: this.mapRepresentativesToUpdate(r.representatives) ?? [],
      id: r.id,
      name: r.name,
      type_id: r.type_id,
      tax_number: r.tax_number,
      party_type_id: r.party_type_id,
      registration_number: r.registration_number ?? null,
      registration_office_id: r.registration_office_id ?? null,
    }));
  }

  private getBankAccountForm(): FormGroup<BankAccountControls> {
    return this.fb.group<BankAccountControls>({
      account_number: this.fb.control<string | null>(null, this.helpersService.bankAccountValidator),
      bic: this.fb.control<string | null>(null, Validators.maxLength(255)),
      bank_name: this.fb.control<string | null>(null, Validators.maxLength(255)),
      lock_bank_account: this.fb.control(false, { nonNullable: true }),
    });
  }

  private getAddressForm(): FormGroup<AddressControls> {
    return this.fb.group<AddressControls>({
      country_id: this.fb.control<number | null>(null),
      postcode: this.fb.control<string | null>(null, Validators.maxLength(255)),
      settlement: this.fb.control<string | null>(null, Validators.maxLength(255)),
      street: this.fb.control<string | null>(null, Validators.maxLength(255)),
    });
  }
}